/**
 * Tenant Create Page
 *
 * Page for creating a new tenant.
 *
 * @fileoverview Admin tenant creation page
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { TenantForm } from '../../../components/admin';
import { useCreateTenant } from '../../../hooks/useAdminTenants';
import type { CreateTenantInput, UpdateTenantInput } from '../../../types/admin';

/**
 * Tenant Create Page Component
 *
 * Form page for creating a new tenant.
 */
export const TenantCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { createTenant, loading, error } = useCreateTenant();

  const handleSubmit = async (data: CreateTenantInput | UpdateTenantInput) => {
    // For create, we only expect CreateTenantInput
    const createData = data as CreateTenantInput;
    try {
      const tenant = await createTenant(createData);
      if (tenant) {
        toast({
          title: 'Tenant created',
          description: `${tenant.name} has been created successfully.`,
          status: 'success',
          duration: 3000,
        });
        navigate(`/admin/tenants/${tenant.id}`);
      }
    } catch (err) {
      // Error is already set in the hook
    }
  };

  const handleCancel = () => {
    navigate('/admin/tenants');
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Box>
        <Heading size="lg">Create Tenant</Heading>
        <Text color="gray.500" mt={1}>
          Add a new tenant to the system
        </Text>
      </Box>

      {/* Form */}
      <TenantForm
        loading={loading}
        error={error}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </VStack>
  );
};

export default TenantCreate;
